/**
 * meshResilience — pure, framework-free helpers the communal hooks use to
 * (a) back off instead of hammering a failing host/preload/load in a tight
 * per-tick loop, (b) turn a raw failure into a human, model-named message
 * for the UI, and (c) emit vendor-neutral telemetry events at the same
 * failure/lifecycle points (the app maps these onto whatever analytics
 * stack it wants — OpenPanel today, see apps/chat/src/telemetry.ts).
 *
 * Everything here is intentionally DOM-free / React-free / mesh-free so it
 * can be unit-tested directly (test/meshResilience.test.ts) with a mock
 * clock + deterministic RNG — the same "keep the decision logic pure and
 * testable" discipline `resolveCommunalShardPlan` already follows.
 */
const DEFAULT_BACKOFF = { baseMs: 2000, capMs: 60_000, factor: 2, jitter: 0.25 };
/**
 * Delay before retry `attempt` (0-based): `min(capMs, baseMs * factor^attempt)`
 * with `±jitter` applied. `rand` is injectable (defaults to `Math.random`)
 * so tests can pin it — `rand: () => 0.5` yields the exact un-jittered
 * schedule (2000, 4000, 8000, …, capped at capMs).
 */
export function computeBackoffMs(attempt, opts = {}, rand = Math.random) {
    const { baseMs, capMs, factor, jitter } = { ...DEFAULT_BACKOFF, ...opts };
    const safeAttempt = Math.max(0, Math.floor(attempt));
    const raw = Math.min(capMs, baseMs * Math.pow(factor, safeAttempt));
    const jittered = raw * (1 + (rand() * 2 - 1) * jitter);
    return Math.max(0, Math.round(Math.min(capMs, jittered)));
}
// ── Failure → human message ─────────────────────────────────────────────
/** Pull an HTTP status code (100–599) out of a raw error/reason string, if
 * one is present ("…failed to fetch shard …: 404", "server returned 404",
 * "… 503 Service Unavailable"). Undefined when the message carries no
 * recognizable status (a non-HTTP failure — worker crash, timeout, etc.). */
export function extractHttpStatus(message) {
    // Only 4xx/5xx — the actual failure codes (404/403/500/503/…). A stray
    // 1xx/2xx/3xx in an error string (e.g. a model-shape number) is almost
    // never a real status and would just produce a misleading "server
    // returned 127"; better to fall back to the raw reason in that case.
    const match = message.match(/\b([45]\d{2})\b/);
    if (!match)
        return undefined;
    const n = Number(match[1]);
    return n >= 400 && n <= 599 ? n : undefined;
}
/**
 * Human, model-named error copy for the host panel / capacity meter —
 * never a silent spinner. Examples:
 *   "Couldn't load model layers 2–13: server returned 404 — retrying in 8s"
 *   "Couldn't load model layers 2–13: worker crashed — retrying in 4s"
 *   "Hosting layers 2–13 keeps failing (server returned 404) — still retrying periodically"
 */
/** First line of a raw error, stripped of stack frames and capped — a
 * worker error often arrives as a multi-line string with an `Error: … at …`
 * stack, which must not be dumped verbatim into a UI card. */
export function cleanReason(reason, maxLen = 160) {
    const firstLine = (reason.split('\n')[0] ?? '').trim();
    return firstLine.length > maxLen ? `${firstLine.slice(0, maxLen)}…` : firstLine;
}
export function describeHostError(input) {
    const range = `${input.layerStart}–${input.layerEnd}`;
    const cause = input.httpStatus !== undefined ? `server returned ${input.httpStatus}` : cleanReason(input.reason) || 'unknown error';
    if (input.retrying) {
        const when = input.nextAttemptInSec !== undefined ? ` — retrying in ${Math.max(0, input.nextAttemptInSec)}s` : ' — retrying shortly';
        return `Couldn't load model layers ${range}: ${cause}${when}`;
    }
    return `Hosting layers ${range} keeps failing (${cause}) — still retrying periodically`;
}
/** Whole seconds until `nextAttemptAtMs`, clamped ≥ 0 — for a countdown. */
export function retryCountdownSec(nextAttemptAtMs, now) {
    if (nextAttemptAtMs === undefined)
        return undefined;
    return Math.max(0, Math.ceil((nextAttemptAtMs - now) / 1000));
}
/** Stable string key for a claim range — used to detect whether the
 * assembly loop's DECISION actually changed between roster ticks (so it
 * only logs/acts on change, instead of re-logging an identical
 * "keeping as-is" decision dozens of times). */
export function claimKey(claim) {
    if (!claim)
        return 'none';
    return `${claim.layerStart}:${claim.layerEnd}:${claim.includeOutput ? 1 : 0}`;
}
export function claimsEqual(a, b) {
    return claimKey(a) === claimKey(b);
}
/** Invoke a (possibly undefined, possibly throwing) sink without ever
 * letting an analytics failure propagate into mesh logic. */
export function emitTelemetry(sink, event) {
    if (!sink)
        return;
    try {
        sink(event);
    }
    catch {
        // analytics is best-effort — never break the app because a sink threw
    }
}
